/**
 * Boot from the persisted session, before React mounts and without waiting for the
 * Firebase SDK's own account lookup.
 *
 * The token is trusted for nothing the server would not trust. Every request still
 * carries it to Hasura, which verifies it, so the persisted token only decides what to
 * ask for first. If the SDK then reports no user, `useAuth` clears everything as it does
 * for a normal sign-out.
 */
import siteConfig from '@config';
import execGraphqlWithFetch from '@utils/gql';

import { startEarlyProductsFetch, type GqlRunner } from '@features/products/earlyProducts';

import { decodeJwtClaims, parsePersistedUser, readPersistedUser, type PersistedSession } from './persistedSession';
import { assembleUser, checkForPermittedRole, convertRawClaims, getUserProfile, setPersistedTokenFallback, type User } from './services/authService';

export interface EarlySession {
  /** The Firebase uid the user was built from; useAuth compares it with the SDK's. */
  uid: string;
  user: User;
}

let started: Promise<EarlySession | undefined> | undefined;

/** A query's text: a string as is, a codegen'd document's `__text` (it has no `loc`), or a
 *  `gql`-tag document's `loc.source.body`. */
export function queryText(query: unknown): string | undefined {
  if (typeof query === 'string') return query;
  const doc = query as { __text?: string; loc?: { source?: { body?: string } } } | null | undefined;
  return doc?.__text ?? doc?.loc?.source?.body;
}

/** `execGraphqlWithFetch` unwraps a document's first root field, which drops every other
 *  field of a multi-field query; passed the query text instead it returns the whole
 *  response. The early product requests need the whole `data`. */
const runWholeResponse: GqlRunner = async <T,>({ query, variables, token }: { query: unknown; variables: Record<string, unknown>; token: string }) => {
  const text = queryText(query);
  if (!text) throw new Error('early products query has no text');
  const json = await execGraphqlWithFetch<{ data?: T; errors?: { message?: string }[] }>({ query: text, variables, token });
  if (json?.errors?.length) throw new Error(json.errors[0]?.message ?? 'GraphQL error');
  if (!json?.data) throw new Error('GraphQL response without data');
  return json.data;
};

/** The pinned rooftop, else the claim's customer. */
function initialCustomerId(claimCustomerId: unknown): number | undefined {
  let pinned = Number.NaN;
  try {
    pinned = parseInt(localStorage?.getItem('rooftopId') ?? '', 10);
  } catch {
    // no storage
  }
  if (Number.isFinite(pinned) && pinned > 0) return pinned;
  const fromClaim = Number(claimCustomerId);
  return Number.isFinite(fromClaim) && fromClaim > 0 ? fromClaim : undefined;
}

async function boot(session: PersistedSession): Promise<EarlySession | undefined> {
  const namespaced = decodeJwtClaims(session.accessToken, siteConfig.firebaseClaimsNamespace);
  if (!namespaced) return undefined;
  const claims = convertRawClaims(namespaced);
  const userId = Number(claims[siteConfig.firebaseClaimsUserId as keyof typeof claims]);
  if (!Number.isFinite(userId) || userId <= 0) return undefined;
  if (!checkForPermittedRole({ claims })) return undefined;
  // Until the SDK has a current user, anything that asks it for a token or claims (the
  // Apollo link, the header rooftop selector) gets the persisted ones instead.
  setPersistedTokenFallback(session.accessToken, namespaced as never);

  // Products first: they are the long pole, and they need nothing from the profile.
  const customerId = initialCustomerId(claims[siteConfig.firebaseClaimsCustomerId as keyof typeof claims]);
  if (customerId) startEarlyProductsFetch(runWholeResponse, session.accessToken, customerId);

  const profile = await getUserProfile(userId, session.accessToken);
  if (!profile) return undefined;
  return { uid: session.uid, user: assembleUser(claims, profile, namespaced as never) };
}

/** Start the early boot. Safe to call more than once; the first call wins. */
export function startEarlySession(): Promise<EarlySession | undefined> {
  if (started) return started;
  started = (async () => {
    try {
      const apiKey = siteConfig.firebaseApiKey as string | undefined;
      if (!apiKey) return undefined;
      const session = parsePersistedUser(await readPersistedUser(apiKey));
      return session ? await boot(session) : undefined;
    } catch (err) {
      console.log('[earlySession] skipped', err);
      return undefined;
    }
  })();
  return started;
}

export function earlySession(): Promise<EarlySession | undefined> {
  return started ?? Promise.resolve(undefined);
}

/** Test seam. */
export function resetEarlySession(): void {
  started = undefined;
}
